import React from 'react';
import { Alert, Box, Divider, Stack, Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';

import { usePatientSearch } from './hooks/use-patient-search';
import { usePatientIntervals } from './hooks/use-patient-intervals';
import { usePatientDocuments } from './hooks/use-patient-documents';
import { PatientSearch } from './by-patient/patient-search';
import { PeriodBasisPicker } from './by-patient/period-basis';
import { DocumentPicker } from './by-patient/document-picker';
import { PacketSummary } from './by-patient/packet-summary';
import { ReviewAndRequestDialog } from './by-patient/review-and-request-dialog';
import { HeldFromBillingToggle } from './held-from-billing-toggle';
import { groupByKind } from './by-patient/grouping';
import { DEFAULT_PACKAGING, PackagingChoices, estimateBytes } from './by-patient/packaging';
import { PatientSearchResult, PeriodBasis } from './by-patient/types';
import { JobRequestDetails } from './mock-data';
import { toIntervalLabel } from './by-patient/format';

/** What the history row needs in order to exist before the server has answered. */
export interface PendingJob {
  documentCount: number;
  patientCount: number;
  sizeBytes?: number;
  requestDetails: JobRequestDetails;
}

interface Props {
  submitting: boolean;
  onSubmit: (payload: Record<string, unknown>, pending: PendingJob) => void;
}

const Step: React.FC<{ n: number; title: string; hint?: string; children: React.ReactNode }> = ({
  n,
  title,
  hint,
  children,
}) => (
  <Box>
    <Stack
      direction="row"
      spacing={1}
      alignItems="baseline"
      sx={{ mb: 1.5 }}
    >
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 600 }}
      >
        {n}. {title}
      </Typography>
      {hint && (
        <Typography
          variant="caption"
          color="text.secondary"
        >
          {hint}
        </Typography>
      )}
    </Stack>
    {children}
  </Box>
);

const safeName = (value: string) => value.replace(/[^\w-]+/g, '_').replace(/_+/g, '_');

/**
 * SQ-166 / SQ-167 / SQ-168 — the By-patient flow: WHO, then WHEN, then WHAT, then the packet.
 *
 * The resident generates every option beneath them, which is why the order differs from
 * By cohort (A2). The cascade rules live here because they are about the relationship
 * between steps, not about any one control:
 *
 *   - a different resident resets the period, the selection and the file name
 *   - a different basis or period resets the selection, because the documents change
 *   - the file name regenerates until the user types in it, then never again
 *   - the ADR reference never resets: it describes the request, not the selection
 */
export const ByPatientPanel: React.FC<Props> = ({ submitting, onSubmit }) => {
  const [query, setQuery] = React.useState('');
  const [patient, setPatient] = React.useState<PatientSearchResult | null>(null);
  const [basis, setBasis] = React.useState<PeriodBasis>('stays');
  const [intervalIds, setIntervalIds] = React.useState<string[]>([]);
  const [dateFrom, setDateFrom] = React.useState<Date | null>(null);
  const [dateTo, setDateTo] = React.useState<Date | null>(null);
  const [deselected, setDeselected] = React.useState<Set<string>>(new Set());
  const [choices, setChoices] = React.useState<PackagingChoices>(DEFAULT_PACKAGING);
  const [fileName, setFileName] = React.useState('');
  const [fileNameEdited, setFileNameEdited] = React.useState(false);
  const [adrReference, setAdrReference] = React.useState('');
  const [reviewOpen, setReviewOpen] = React.useState(false);
  //  SQ-181: ordinary state, deliberately. Every request starts excluding held documents,
  //  so an internal review cannot silently set up the next external submission to
  //  over-disclose. THIN-8113: do not put this in localStorage or user preferences.
  const [includeHeld, setIncludeHeld] = React.useState(false);

  const search = usePatientSearch(query);
  const { intervals, loading: intervalsLoading } = usePatientIntervals(patient?.uuid ?? null);

  const isoFrom = dateFrom ? format(dateFrom, 'yyyy-MM-dd') : null;
  const isoTo = dateTo ? format(dateTo, 'yyyy-MM-dd') : null;

  const {
    documents,
    loading: documentsLoading,
    heldWithheldCount,
  } = usePatientDocuments({
    patientUuid: patient?.uuid ?? null,
    basis,
    intervalIds,
    dateFrom: isoFrom,
    dateTo: isoTo,
    includeHeldFromBilling: includeHeld,
  });

  // ── Cascade ──

  const handleSelectPatient = (next: PatientSearchResult) => {
    setQuery(`${next.lastName}, ${next.firstName}`);
    setPatient(next);
    setBasis('stays');
    setIntervalIds([]);
    setDeselected(new Set());
    setDateFrom(null);
    setDateTo(null);
    setIncludeHeld(false);
  };

  //  Defaults once the resident's periods arrive: one interval ticks itself, several
  //  tick the newest. Specific dates default to the resident's whole record, which makes
  //  "everything on file" the no-op rather than a separate option to find (C4).
  React.useEffect(() => {
    if (!patient) return;
    const rows = basis === 'coverage' ? intervals.coverage : intervals.stays;
    if (basis === 'dates') {
      setDateFrom(intervals.earliestDos ? parseISO(intervals.earliestDos) : null);
      setDateTo(intervals.latestDos ? parseISO(intervals.latestDos) : null);
      setIntervalIds([]);
    } else {
      setIntervalIds(rows.length > 0 ? [rows[0].id] : []);
    }
    setDeselected(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient?.uuid, basis, intervals]);

  const handleToggleInterval = (id: string) => {
    setIntervalIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    setDeselected(new Set());
  };

  //  Deselection is tracked rather than selection: the documents list is replaced
  //  Everything in this list is selected unless it has been ticked off, held documents
  //  included.
  //
  //  Held documents used to be the exception: they were opted *into* by name while
  //  everything else was deselected by name, on the reasoning that someone who ticks
  //  "include documents held from billing" in order to *see* what is held has not thereby
  //  agreed to send it.
  //
  //  That reading does not survive contact with the control's own label. It says "Also
  //  include documents held from billing" — ticking it is the agreement, and the list is
  //  where you then take things back out. Two opposite conventions in one list also meant
  //  the held rows appeared unticked among ticked ones with nothing to explain the
  //  difference, and every one of them cost a second click. Reported from QA as the
  //  documents not being included at all.
  //
  //  Untickable as before: a held document is in `deselected` the moment it is unticked,
  //  exactly like any other. What is gone is the second click needed to put it in.
  //
  //  Derived rather than defaulted by an effect, because the period cascade clears
  //  `deselected` on its own schedule — an effect writing into the same set is a race, and
  //  the losing side is a held document silently selected.
  const selected = React.useMemo(
    () => new Set(documents.filter((d) => !deselected.has(d.uuid)).map((d) => d.uuid)),
    [documents, deselected]
  );

  const groups = React.useMemo(() => groupByKind(documents), [documents]);

  const setOne = (uuid: string, on: boolean) => {
    setDeselected((prev) => {
      const next = new Set(prev);
      if (on) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  };

  const toggleDocument = (uuid: string) => setOne(uuid, !selected.has(uuid));

  const toggleGroup = (group: { documents: { uuid: string }[] }) => {
    const allOn = group.documents.every((d) => selected.has(d.uuid));
    group.documents.forEach((d) => setOne(d.uuid, !allOn));
  };

  const selectAll = () => setDeselected(new Set());
  const clearAll = () => setDeselected(new Set(documents.map((d) => d.uuid)));

  // ── Labels ──

  const patientLabel = patient ? `${patient.lastName}, ${patient.firstName}` : '';

  const periodLabel = React.useMemo(() => {
    if (!patient) return '';
    if (basis === 'dates') {
      return isoFrom && isoTo ? toIntervalLabel(isoFrom, isoTo) : '';
    }
    const rows = basis === 'coverage' ? intervals.coverage : intervals.stays;
    const picked = rows.filter((r) => intervalIds.includes(r.id));
    if (picked.length === 0) return '';
    if (picked.length === 1) return toIntervalLabel(picked[0].start, picked[0].end);
    return `${picked.length} periods`;
  }, [patient, basis, intervals, intervalIds, isoFrom, isoTo]);

  //  Regenerates on every selection change until the user edits it, then stops for good.
  React.useEffect(() => {
    if (fileNameEdited || !patient) return;
    const parts = [safeName(`${patient.lastName}_${patient.firstName}`), patient.mrn].filter(
      Boolean
    );
    if (periodLabel) parts.push(safeName(periodLabel));
    setFileName(parts.join('_'));
  }, [patient, periodLabel, fileNameEdited]);

  const hasPeriodSelected = basis === 'dates' ? Boolean(isoFrom && isoTo) : intervalIds.length > 0;

  //  The window the selection actually covers. For a stay or a coverage period that is the
  //  span of the ticked rows, not the date pickers — those are only live on the dates basis.
  const [rangeFrom, rangeTo] = React.useMemo(() => {
    if (basis === 'dates') return [isoFrom ?? '', isoTo ?? ''];
    const rows = (basis === 'coverage' ? intervals.coverage : intervals.stays).filter((r) =>
      intervalIds.includes(r.id)
    );
    if (rows.length === 0) return ['', ''];
    const starts = rows.map((r) => r.start).sort();
    //  An open interval runs to today, which is what the row should say it covers.
    const ends = rows.map((r) => r.end ?? format(new Date(), 'yyyy-MM-dd')).sort();
    return [starts[0], ends[ends.length - 1]];
  }, [basis, intervals, intervalIds, isoFrom, isoTo]);

  const handleRequest = () => {
    //  The nested shape useSubmitDownloadJob expects; it flattens before dispatching.
    const payload = {
      job_name: fileName,
      filters: {
        // SQ-163: the authoritative selection. Filters cannot express "all of these
        // except that one", so the resolved list travels instead and the resolver
        // uses it verbatim.
        document_uuids: [...selected],
        patient_ids: patient ? [patient.uuid] : [],
        ...(adrReference.trim() && { adr_reference: adrReference.trim() }),
        ...(isoFrom && { date_from: isoFrom }),
        ...(isoTo && { date_to: isoTo }),
        // SQ-181: travels even though the selection is explicit, so request_details
        // records what the list was drawn with.
        ...(includeHeld && { include_held_from_billing: true }),
      },
      packaging_options: {
        deliver_as: choices.deliverAs,
        group_by: choices.groupBy,
        document_handling: choices.documentHandling,
        order_by: choices.orderBy,
        ...(choices.includeAuditLog && { include_audit_log: true }),
      },
    };

    //  So the request shows up in the history as QUEUED straight away. This is not only
    //  reassurance: useDownloadJobs polls only while an active job is in the list, so
    //  without the row the request is both invisible and unpolled until a manual refresh.
    const pending: PendingJob = {
      documentCount: selected.size,
      patientCount: patient ? 1 : 0,
      sizeBytes: estimateBytes(selected.size),
      requestDetails: {
        filters_applied: {
          date_from: rangeFrom,
          date_to: rangeTo,
          ...(patient && { patient_ids: [patient.uuid] }),
        },
        file_organization: {
          //  The basis is what "scope" means in this mode — stays, coverage or dates.
          date_scope: basis,
          group_by: choices.groupBy,
          document_handling: choices.documentHandling,
          order_by: choices.orderBy,
          deliver_as: choices.deliverAs,
        },
        selection_mode: 'explicit',
        selected_document_count: selected.size,
        ...(adrReference.trim() && { adr_reference: adrReference.trim() }),
      },
    };

    onSubmit(payload, pending);
    setReviewOpen(false);
  };

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={3}
      alignItems="flex-start"
    >
      <Stack
        spacing={3}
        divider={<Divider flexItem />}
        sx={{
          flex: { md: '0 0 calc(60% - 12px)' },
          width: '100%',
          minWidth: 0,
          border: '1px solid',
          borderColor: '#dfe2e7',
          borderRadius: 1,
          p: 2.5,
        }}
      >
        <Step
          n={1}
          title="Patient"
          hint="search by name or MRN"
        >
          <PatientSearch
            query={query}
            onQueryChange={setQuery}
            results={search.results}
            loading={search.loading}
            searched={search.searched}
            minQueryLength={search.minQueryLength}
            selected={patient}
            onSelect={handleSelectPatient}
          />
        </Step>

        {patient && (
          <>
            <Step
              n={2}
              title="Period"
              // hint="one basis at a time"
            >
              <PeriodBasisPicker
                intervals={intervals}
                loading={intervalsLoading}
                basis={basis}
                onBasisChange={setBasis}
                selectedIds={intervalIds}
                onToggleInterval={handleToggleInterval}
                dateFrom={dateFrom}
                dateTo={dateTo}
                onDateFromChange={setDateFrom}
                onDateToChange={setDateTo}
              />
            </Step>

            <Step
              n={3}
              title="Documents"
            >
              <DocumentPicker
                groups={groups}
                loading={documentsLoading}
                selected={selected}
                onToggleDocument={toggleDocument}
                onToggleGroup={toggleGroup}
                onSelectAll={selectAll}
                onClear={clearAll}
                hasPeriodSelected={hasPeriodSelected}
              />
              {hasPeriodSelected && (
                <HeldFromBillingToggle
                  checked={includeHeld}
                  onChange={setIncludeHeld}
                  withheldCount={heldWithheldCount}
                />
              )}
            </Step>
          </>
        )}
      </Stack>

      {patient && (
        <Box
          sx={{
            flex: { md: '0 0 calc(40% - 12px)' },
            width: '100%',
            minWidth: 0,
            position: { md: 'sticky' },
            top: { md: 24 },
            border: '1px solid',
            borderColor: '#dfe2e7',
            borderRadius: 1,
            p: 2.5,
          }}
        >
          {patient.documentCount === 0 ? (
            <Alert severity="warning">
              {patientLabel} has no documents on file in this facility.
            </Alert>
          ) : (
            <PacketSummary
              patientLabel={patientLabel}
              periodLabel={periodLabel}
              documentCount={selected.size}
              choices={choices}
              fileName={fileName}
              onFileNameChange={(v) => {
                setFileName(v);
                setFileNameEdited(true);
              }}
              adrReference={adrReference}
              onAdrReferenceChange={setAdrReference}
              onReview={() => setReviewOpen(true)}
            />
          )}
        </Box>
      )}

      <ReviewAndRequestDialog
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        choices={choices}
        onChange={setChoices}
        groups={groups}
        selected={selected}
        fileName={fileName}
        patientLabel={patientLabel}
        submitting={submitting}
        onRequest={handleRequest}
      />
    </Stack>
  );
};